import { useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../components/ui"
import useGa4Property from "../hooks/useGa4Property"
import { supabase } from "../supabaseClient"
import { useUser } from "../UserContext"
import { formatDate, formatDateTime, formatNumber } from "../utils/formatters"
import "./GA4EventAudit.css"

const cx = (...classes) => classes.filter(Boolean).join(" ")

export default function GA4EventAudit() {
  const navigate = useNavigate()
  const { search } = useLocation()
  const propertyId = new URLSearchParams(search).get("id")
  const [eventAudit, setEventAudit] = useState([])
  const [auditLoading, setAuditLoading] = useState(false)
  const [auditError, setAuditError] = useState(null)
  const [expandedOwners, setExpandedOwners] = useState({})
  const [lastUserAudit, setLastUserAudit] = useState(null)
  const [auditSaveError, setAuditSaveError] = useState(null)
  const [auditSaveMessage, setAuditSaveMessage] = useState("")
  const [auditSaving, setAuditSaving] = useState(false)
  const [expectedUpdating, setExpectedUpdating] = useState({})
  const [expectedUpdateError, setExpectedUpdateError] = useState(null)
  const user = useUser()
  const {
    property: propInfo,
    loading: propertyLoading,
    error: propertyError,
  } = useGa4Property(propertyId)

  useEffect(() => {
    if (propertyLoading) return

    if (propertyError || !propInfo?.id) {
      setEventAudit([])
      setAuditError(propertyError || null)
      setAuditLoading(false)
      setLastUserAudit(null)
      return
    }

    let cancelled = false

    const loadAudit = async () => {
      setAuditLoading(true)
      setAuditError(null)
      try {
        const { data: audit, error } = await supabase
          .from("ga4_event_audit_results")
          .select(
            "id, event_owner, first_detected, last_detected, event_count, event_details, monthly_breakdown, expected"
          )
          .eq("ga4_property_id", propInfo.id)
          .order("event_owner", { ascending: true })
        if (error) throw error
        if (cancelled) return
        setEventAudit(
          (audit || []).map((row) => ({
            ...row,
            id: typeof row?.id === "string" ? row.id : null,
            expected: row?.expected === true,
            event_details: Array.isArray(row.event_details)
              ? row.event_details.map((detail) => ({
                  eventName:
                    typeof detail?.eventName === "string"
                      ? detail.eventName
                      : "",
                  eventCount:
                    typeof detail?.eventCount === "number"
                      ? detail.eventCount
                      : null,
                }))
              : [],
            monthly_breakdown: Array.isArray(row.monthly_breakdown)
              ? row.monthly_breakdown
              : [],
          }))
        )
      } catch (err) {
        if (cancelled) return
        console.error("Failed to load GA4 event audit", err)
        setEventAudit([])
        setAuditError(err)
      } finally {
        if (!cancelled) setAuditLoading(false)
      }
    }

    loadAudit()

    return () => {
      cancelled = true
    }
  }, [propInfo?.id, propertyLoading, propertyError])

  useEffect(() => {
    if (propertyLoading) return

    if (propertyError || !propInfo?.id) {
      setLastUserAudit(null)
      return
    }

    let cancelled = false

    const loadLastAudit = async () => {
      try {
        const { data, error } = await supabase
          .from("ga4_event_owner_audits")
          .select("audited_at")
          .eq("ga4_property_id", propInfo.id)
        if (error) throw error
        if (cancelled) return
        let latest = null
        if (Array.isArray(data)) {
          for (const row of data) {
            const value = row?.audited_at
            if (!value || typeof value !== "string") continue
            if (!latest || Date.parse(value) > Date.parse(latest)) {
              latest = value
            }
          }
        }
        setLastUserAudit(latest)
      } catch (err) {
        if (cancelled) return
        console.error("Failed to load GA4 event owner audits", err)
        setLastUserAudit(null)
      }
    }

    loadLastAudit()

    return () => {
      cancelled = true
    }
  }, [propInfo?.id, propertyLoading, propertyError])

  useEffect(() => {
    setAuditSaveError(null)
    setAuditSaveMessage("")
  }, [propInfo?.id])

  useEffect(() => {
    setExpandedOwners({})
  }, [eventAudit])

  useEffect(() => {
    setExpectedUpdateError(null)
  }, [propInfo?.id])

  const loading = propertyLoading || auditLoading

  const allOwnersExpanded = useMemo(() => {
    if (!eventAudit.length) return false
    return eventAudit.every((entry) => expandedOwners[entry.event_owner])
  }, [eventAudit, expandedOwners])

  const handleToggleAll = () => {
    if (allOwnersExpanded) {
      setExpandedOwners({})
      return
    }
    const nextState = {}
    for (const entry of eventAudit) {
      nextState[entry.event_owner] = true
    }
    setExpandedOwners(nextState)
  }

  const toggleOwner = (owner) => {
    setExpandedOwners((prev) => ({
      ...prev,
      [owner]: !prev[owner],
    }))
  }

  const updateExpected = async (id, value) => {
    if (!id) return

    setExpectedUpdateError(null)
    setExpectedUpdating((prev) => ({
      ...prev,
      [id]: true,
    }))

    try {
      const { error } = await supabase
        .from("ga4_event_audit_results")
        .update({ expected: value })
        .eq("id", id)
      if (error) throw error
      setEventAudit((prev) =>
        prev.map((row) =>
          row.id === id
            ? {
                ...row,
                expected: value,
              }
            : row,
        ),
      )
    } catch (err) {
      console.error("Failed to update expected event owner", err)
      setExpectedUpdateError(
        "Failed to update expected event owner. Please try again.",
      )
    } finally {
      setExpectedUpdating((prev) => {
        const next = { ...prev }
        delete next[id]
        return next
      })
    }
  }

  const handleMarkAudited = async () => {
    if (!propInfo?.id || !user?.id || auditSaving) return

    setAuditSaving(true)
    setAuditSaveError(null)
    setAuditSaveMessage("")
    try {
      const { data, error } = await supabase
        .from("ga4_event_owner_audits")
        .insert({
          ga4_property_id: propInfo.id,
          user_id: user.id,
          owner_count: eventAudit.length,
        })
        .select("audited_at")
        .single()
      if (error) throw error
      setLastUserAudit(data?.audited_at || null)
      setAuditSaveMessage("Event owners marked as audited.")
    } catch (err) {
      console.error("Failed to save event owner audit", err)
      setAuditSaveError(err)
    } finally {
      setAuditSaving(false)
    }
  }

  return (
    <div className="page-container ga4-event-audit ui-stack ui-stack--xl">
      <div className="ui-page-header ga4-event-audit__header">
        <h2 className="ga4-event-audit__title">Event Audit</h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/ga4-property?id=${encodeURIComponent(propertyId)}`)}
        >
          Back to GA4 property
        </Button>
      </div>
      {loading ? (
        <p className="ga4-event-audit__message">Loading event audit…</p>
      ) : propertyError ? (
        <p className="ga4-event-audit__error">Failed to load property details.</p>
      ) : auditError ? (
        <p className="ga4-event-audit__error">Failed to load event audit data.</p>
      ) : (
        <div className="ga4-event-audit__content ui-stack ui-stack--lg">
          {propInfo ? (
            <div className="ga4-event-audit__property">
              <h3 className="ga4-event-audit__property-name">{propInfo.property_name}</h3>
              <span className="ga4-event-audit__property-id">{propInfo.property_id}</span>
            </div>
          ) : (
            <p className="ga4-event-audit__message">Property not found.</p>
          )}
          <div className="ga4-event-audit__toolbar ui-stack ui-stack--row ui-stack--wrap">
            <span
              className={cx(
                "ga4-event-audit__last-audit",
                lastUserAudit ? "is-recorded" : "is-empty",
              )}
            >
              {lastUserAudit
                ? `Last user audit: ${formatDateTime(lastUserAudit)}`
                : "Event owners have not been audited by a user yet."}
            </span>
            <Button
              onClick={handleMarkAudited}
              disabled={!propInfo?.id || !user?.id || auditSaving}
            >
              {auditSaving ? "Saving…" : "Mark event owners as audited"}
            </Button>
            {auditSaveError ? (
              <span className="ga4-event-audit__error">Failed to save audit. Please try again.</span>
            ) : null}
            {auditSaveMessage && !auditSaveError ? (
              <span className="ga4-event-audit__success">{auditSaveMessage}</span>
            ) : null}
          </div>
          {expectedUpdateError ? (
            <p className="ga4-event-audit__error">{expectedUpdateError}</p>
          ) : null}
          {eventAudit.length ? (
            <div className="ga4-event-audit__table-section ui-stack ui-stack--lg">
              <div className="ga4-event-audit__section-header ui-page-header">
                <h3 className="ga4-event-audit__section-title">Event Owner Audit</h3>
                <Button variant="ghost" size="sm" onClick={handleToggleAll}>
                  {allOwnersExpanded ? "Hide all events" : "Expand all events"}
                </Button>
              </div>
              <TableContainer className="ga4-event-audit__table-container">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Event Owner</TableHeaderCell>
                      <TableHeaderCell>First Detected</TableHeaderCell>
                      <TableHeaderCell>Last Detected</TableHeaderCell>
                      <TableHeaderCell align="right">Total Events</TableHeaderCell>
                      <TableHeaderCell>Expected Event Owner</TableHeaderCell>
                      <TableHeaderCell>Events</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {eventAudit.map((e) => {
                      const isUpdating = Boolean(e.id && expectedUpdating[e.id])
                      const isExpanded = Boolean(expandedOwners[e.event_owner])
                      return (
                        <TableRow
                          key={e.id || e.event_owner}
                          className={cx(
                            "ga4-event-audit__row",
                            e.expected
                              ? "ga4-event-audit__row--expected"
                              : "ga4-event-audit__row--unexpected",
                          )}
                        >
                          <TableCell>
                            <span className="ga4-event-audit__owner">{e.event_owner}</span>
                          </TableCell>
                          <TableCell>
                            <span className="ga4-event-audit__date">{formatDate(e.first_detected)}</span>
                          </TableCell>
                          <TableCell>
                            <span className="ga4-event-audit__date">{formatDate(e.last_detected)}</span>
                          </TableCell>
                          <TableCell align="right">
                            <span className="ga4-event-audit__metric">{formatNumber(e.event_count)}</span>
                          </TableCell>
                          <TableCell>
                            <div className="ga4-event-audit__status">
                              <Badge
                                className={cx(
                                  "ga4-event-audit__status-badge",
                                  e.expected
                                    ? "ga4-event-audit__status-badge--expected"
                                    : "ga4-event-audit__status-badge--unexpected",
                                )}
                              >
                                {e.expected ? "Expected" : "Not expected"}
                              </Badge>
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => updateExpected(e.id, !e.expected)}
                                aria-pressed={e.expected}
                                disabled={!e.id || isUpdating}
                              >
                                {isUpdating
                                  ? "Saving…"
                                  : e.expected
                                  ? "Mark as not expected"
                                  : "Mark as expected"}
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell>
                            {e.event_details.length ? (
                              <div className="ga4-event-audit__events">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => toggleOwner(e.event_owner)}
                                  aria-expanded={isExpanded}
                                >
                                  {isExpanded ? "Hide events" : "Show events"}
                                </Button>
                                {isExpanded ? (
                                  <ul className="ga4-event-audit__event-list">
                                    {e.event_details.map((detail, index) => {
                                      const label = detail.eventName || "(not set)"
                                      return (
                                        <li
                                          key={`${e.event_owner}-${label}-${detail.eventCount ?? "none"}-${index}`}
                                          className="ga4-event-audit__event"
                                        >
                                          <span className="ga4-event-audit__event-name">{label}</span>
                                          <span className="ga4-event-audit__event-count">
                                            {detail.eventCount === null
                                              ? "—"
                                              : formatNumber(detail.eventCount)}
                                          </span>
                                        </li>
                                      )
                                    })}
                                  </ul>
                                ) : null}
                              </div>
                            ) : (
                              <span className="ga4-event-audit__empty">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <MonthlyBreakdown data={eventAudit} />
            </div>
          ) : (
            <p className="ga4-event-audit__message">No event audit results saved.</p>
          )}
        </div>
      )}
    </div>
  )
}

function MonthlyBreakdown({ data }) {
  const [monthFilter, setMonthFilter] = useState("")
  const [eventFilter, setEventFilter] = useState("")

  const monthlyData = useMemo(() => {
    const map = new Map()
    for (const entry of data) {
      const breakdown = Array.isArray(entry.monthly_breakdown)
        ? entry.monthly_breakdown
        : []
      for (const month of breakdown) {
        const monthKey = month.yearMonth
        if (!monthKey) continue
        const hasAggregateSessions = Object.prototype.hasOwnProperty.call(
          month,
          "totalSessions"
        )
        const monthEntry = map.get(monthKey) || {
          entries: [],
          totalEvents: 0,
          totalSessions: hasAggregateSessions ? null : 0,
        }
        const events = Array.isArray(month.events)
          ? month.events.map((event) => ({
              eventName: event.eventName || "(not set)",
              eventCount:
                typeof event.eventCount === "number" ? event.eventCount : null,
            }))
          : []
        const totalCount = typeof month.totalCount === "number" ? month.totalCount : 0
        const rawSessionCount =
          typeof month.sessionCount === "number" ? month.sessionCount : null
        const aggregateSessions = hasAggregateSessions
          ? typeof month.totalSessions === "number"
            ? month.totalSessions
            : null
          : rawSessionCount
        const ownerSessionCount = hasAggregateSessions ? null : rawSessionCount
        monthEntry.entries.push({
          owner: entry.event_owner,
          totalCount,
          sessionCount: ownerSessionCount,
          events,
        })
        monthEntry.totalEvents += totalCount
        if (hasAggregateSessions) {
          monthEntry.totalSessions =
            typeof aggregateSessions === "number" ? aggregateSessions : null
        } else if (typeof aggregateSessions === "number") {
          monthEntry.totalSessions += aggregateSessions
        }
        map.set(monthKey, monthEntry)
      }
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([monthKey, info]) => ({
        monthKey,
        totalEvents: info.totalEvents,
        totalSessions: info.totalSessions,
        entries: info.entries.sort((a, b) => a.owner.localeCompare(b.owner)),
      }))
  }, [data])

  const monthOptions = useMemo(() => {
    return monthlyData.map((month) => ({
      value: month.monthKey,
      label: formatYearMonth(month.monthKey),
    }))
  }, [monthlyData])

  const filteredMonthlyData = useMemo(() => {
    const normalizedMonthFilter = monthFilter.trim().toLowerCase()
    const normalizedEventFilter = eventFilter.trim().toLowerCase()

    return monthlyData
      .map((month) => {
        const label = formatYearMonth(month.monthKey)
        const monthMatches =
          !normalizedMonthFilter ||
          month.monthKey.toLowerCase().includes(normalizedMonthFilter) ||
          label.toLowerCase().includes(normalizedMonthFilter)
        if (!monthMatches) return null

        const entries = month.entries
          .map((entry) => {
            if (!normalizedEventFilter) {
              return { ...entry }
            }
            const matchesEvent = entry.events.some((event) => {
              const name = (event.eventName || "").toLowerCase()
              return name.includes(normalizedEventFilter)
            })
            if (!matchesEvent) return null
            const filteredEvents = entry.events.filter((event) => {
              const name = (event.eventName || "").toLowerCase()
              return name.includes(normalizedEventFilter)
            })
            return {
              ...entry,
              events: filteredEvents,
            }
          })
          .filter(Boolean)

        if (!entries.length) return null

        const totalForDisplay = normalizedEventFilter
          ? entries.reduce((sum, entry) => {
              return (
                sum +
                entry.events.reduce((eventSum, event) => {
                  const value =
                    typeof event.eventCount === "number" ? event.eventCount : 0
                  return eventSum + value
                }, 0)
              )
            }, 0)
          : month.totalEvents

        return {
          monthKey: month.monthKey,
          label,
          totalEvents: month.totalEvents,
          totalSessions: month.totalSessions,
          displayedEventTotal: totalForDisplay,
          entries,
        }
      })
      .filter(Boolean)
  }, [monthlyData, monthFilter, eventFilter])

  if (!monthlyData.length) return null

  return (
    <div style={{ marginTop: "2rem" }}>
      <h3>Event Exploration</h3>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "1rem",
          marginTop: "0.5rem",
          alignItems: "center",
        }}
      >
        <label style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
          <span style={{ fontSize: "0.9rem", fontWeight: 600 }}>Month filter</span>
          <input
            list="ga4-month-filter-options"
            value={monthFilter}
            onChange={(event) => setMonthFilter(event.target.value)}
            placeholder="Type a month (e.g. 2024-01 or January)"
            style={{ padding: "0.25rem", minWidth: "220px" }}
          />
          <datalist id="ga4-month-filter-options">
            {monthOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </datalist>
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
          <span style={{ fontSize: "0.9rem", fontWeight: 600 }}>Event details filter</span>
          <input
            value={eventFilter}
            onChange={(event) => setEventFilter(event.target.value)}
            placeholder="Type part of an event name"
            style={{ padding: "0.25rem", minWidth: "220px" }}
          />
        </label>
        {(monthFilter || eventFilter) && (
          <button
            type="button"
            onClick={() => {
              setMonthFilter("")
              setEventFilter("")
            }}
            style={{ padding: "0.35rem 0.75rem" }}
          >
            Clear filters
          </button>
        )}
      </div>
      {filteredMonthlyData.length ? (
        filteredMonthlyData.map((month) => (
          <div key={month.monthKey} style={{ marginTop: "1rem" }}>
            <h4 style={{ marginBottom: "0.25rem" }}>{month.label}</h4>
            <p style={{ marginTop: 0, fontStyle: "italic" }}>
              Total events: {formatNumber(month.displayedEventTotal)}
              {typeof month.totalSessions === "number" && month.totalSessions > 0 && (
                <>
                  {" "}• Total sessions: {formatNumber(month.totalSessions)}
                </>
              )}
            </p>
            <table className="ui-table" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th style={{ width: "22%" }}>Event Owner</th>
                  <th style={{ width: "22%" }}>Event Name</th>
                  <th style={{ width: "10%" }}>Event Count</th>
                </tr>
              </thead>
              <tbody>
                {month.entries.flatMap((entry) => {
                  const events = entry.events.length
                    ? entry.events
                    : [
                        {
                          eventName: "No events recorded",
                          eventCount: null,
                        },
                      ]
                  return events.map((event) => (
                    <tr
                      key={`${month.monthKey}-${entry.owner}-${event.eventName}-${event.eventCount ?? "none"}`}
                    >
                      <td>{entry.owner}</td>
                      <td>{event.eventName}</td>
                      <td>
                        {event.eventCount === null
                          ? "—"
                          : formatNumber(event.eventCount)}
                      </td>
                    </tr>
                  ))
                })}
              </tbody>
            </table>
        </div>
        ))
      ) : (
        <p style={{ marginTop: "1rem", fontStyle: "italic" }}>
          No events match the current filters.
        </p>
      )}
    </div>
  )
}

function formatYearMonth(value) {
  if (!value || typeof value !== "string") return value || "—"
  const parts = value.split("-")
  if (parts.length !== 2) return value
  const [year, month] = parts
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, 1))
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(date)
}
